import { useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * Cover image uploader used in admin editors.
 * Value is a public URL (e.g. /api/files/marcuscylar/covers/uuid.jpg) or absolute URL.
 */
export default function ImageUploader({ value, onChange, testid = "image-uploader" }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large. Max 8MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const displayUrl = value
    ? value.startsWith("http")
      ? value
      : `${process.env.REACT_APP_BACKEND_URL}${value}`
    : null;

  return (
    <div className="space-y-3" data-testid={testid}>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Cover image</label>
      {displayUrl ? (
        <div className="relative group rounded-xl overflow-hidden border border-border">
          <img src={displayUrl} alt="cover" className="w-full h-40 object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-remove-btn`}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-background/90 border border-border opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <label className="flex items-center justify-center gap-2 w-full h-40 rounded-xl border-2 border-dashed border-border cursor-pointer hover:border-foreground/40 transition-colors">
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : (
            <>
              <Upload className="w-4 h-4 text-muted-foreground" />
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Click to upload (JPG, PNG, WEBP · max 8MB)
              </span>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${testid}-input`}
            onChange={(e) => upload(e.target.files?.[0])}
            disabled={uploading}
          />
        </label>
      )}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-mono">or paste URL:</span>
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          data-testid={`${testid}-url-input`}
          className="flex-1 bg-transparent border-b border-border px-2 py-1 outline-none focus:border-foreground transition-colors font-mono text-xs"
        />
      </div>
    </div>
  );
}
